import { FullStory } from '@fullstory/browser';
import { portalStoreToken, PortalUser } from '@servicetitan/cp-ui';
import { inject, injectable } from '@servicetitan/react-ioc';
import { AuthApi, AuthenticationInfo, LoginType, PropertyOwnerType } from 'api';
import Cookies from 'js-cookie';
import { computed, makeObservable, observable, runInAction } from 'mobx';
import { WebPortalStore } from '../../common/stores/portal.store';

export enum LoginResult {
    OK = 0,
    Invalid = 1,
    Restricted = 2,
}

const loginDataCookieName = 'ST-Customer-Portal-Frontend';

export type LoginlessLoginType = 'EstimateLink' | 'PaymentLink' | 'ServiceAgreementLink';

interface LoginlessLoginData {
    LoginType: LoginlessLoginType;
    Email: string | null;
}

@injectable()
export class PortalAuthStore {
    @observable authorizing = true;
    @observable authorized = false;
    @observable authorizedLoginless = false;
    @observable loginlessType?: LoginlessLoginType;
    @observable redirect?: string;
    @observable validateUserLoading = false;

    @computed get isAnonymousPayments() {
        return this.loginlessType === 'PaymentLink';
    }
    @computed get isAnonymousEstimates() {
        return this.loginlessType === 'EstimateLink';
    }
    @computed get isAnonymousServiceAgreements() {
        return this.loginlessType === 'ServiceAgreementLink';
    }

    constructor(
        @inject(AuthApi) private authApi: AuthApi,
        @inject(portalStoreToken) public portalStore: WebPortalStore
    ) {
        makeObservable(this);
    }

    init = async (redirect?: string) => {
        runInAction(() => {
            this.authorized = false;
            this.redirect = redirect;
        });

        await this.validate();
    };

    validate = async () => {
        if (this.validateUserLoading) {
            return;
        }

        runInAction(() => {
            this.authorizing = true;
            this.validateUserLoading = true;
        });

        const user = await this.validateUser();

        if (user) {
            runInAction(() => {
                this.authorizing = false;
                this.authorized = true;
            });

            this.portalStore.setUser(user);

            FullStory('setIdentity', {
                uid: user.id.toString(),
                properties: {
                    userName: user.name,
                    email: user.name,
                    tenantName: (window as any).PortalSettings?.tenantName || null,
                    tenantId: (window as any).PortalSettings?.tenantId || null,
                },
            });
        } else {
            const cookie = Cookies.get(loginDataCookieName);
            if (cookie) {
                try {
                    const loginData: LoginlessLoginData = JSON.parse(window.atob(cookie));
                    FullStory('setProperties', {
                        type: 'user',
                        properties: {
                            email: loginData.Email ?? null,
                            tenantName: (window as any).PortalSettings?.tenantName || null,
                            tenantId: (window as any).PortalSettings?.tenantId || null,
                        },
                    });
                    runInAction(() => {
                        this.authorizing = false;
                        this.authorizedLoginless = true;
                        this.loginlessType = loginData.LoginType;
                    });
                } catch {
                    // skip error
                }
            }
        }

        runInAction(() => {
            this.authorizing = false;
            this.validateUserLoading = false;
        });
    };

    validateAnyAccount = async () => {
        return this.validateUser(true);
    };

    login = async (
        username: string,
        password: string,
        keepSigned: boolean
    ): Promise<LoginResult> => {
        let result;
        try {
            result = await this.authApi.client.authenticate(
                new AuthenticationInfo({
                    username,
                    password,
                    keepSigned,
                })
            );
        } catch {
            return LoginResult.Invalid;
        }

        if (result?.type === LoginType.Restricted) {
            return LoginResult.Restricted;
        }

        runInAction(() => {
            this.authorizing = true;
            this.authorized = false;
        });

        return LoginResult.OK;
    };

    loginWithGoogle = async (idToken: string): Promise<LoginResult> => {
        try {
            await this.authApi.client.authenticateWithGoogle(idToken);
        } catch {
            return LoginResult.Invalid;
        }

        runInAction(() => {
            this.authorizing = true;
            this.authorized = false;
        });

        return LoginResult.OK;
    };

    loginWithMicrosoft = async (token: string): Promise<LoginResult> => {
        try {
            await this.authApi.client.authenticateWithMicrosoft(token);
        } catch {
            return LoginResult.Invalid;
        }

        runInAction(() => {
            this.authorizing = true;
            this.authorized = false;
        });

        return LoginResult.OK;
    };

    logout = async () => {
        await this.authApi.client.logout().catch(() => null);

        runInAction(() => {
            this.authorizing = false;
            this.authorized = false;
            this.redirect = undefined;
        });

        this.portalStore.removeUser();
    };

    private validateUser = async (anyAccount = false): Promise<PortalUser['data'] | undefined> => {
        try {
            const result = await this.authApi.client.validate();

            if (anyAccount || result.loginType === LoginType.Full) {
                return {
                    id: result.userId,
                    name: result.userName ?? '',
                    isResidential: result.propertyOwnerType === PropertyOwnerType.Residential,
                };
            }
        } catch {
            // skip
        }
    };
}
